import * as z from "zod";

import React, { FormEvent, useState, useTransition } from "react";
import { Bounce, Fade } from "react-awesome-reveal";
import toast from "react-hot-toast";
import { FaEye, FaCheck, FaEyeSlash } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { RegisterSchema } from "../../lib/schemas";
import { useNavigate } from "react-router-dom";
import { RegisTerTypes } from "../../services/auth/type";
import { RegisterApi } from "../../services/auth/auth.service";
import {
  AUTH_BG,
  JUDICIAL_IMAGE,
  LOGO_BLUE,
  LOGO_V2,
  ORANGE_LOGO,
} from "../../utils-func/image_exports";
import ButtonV2 from "../../shared/components/buttonV2";
import { clearChats, setSettings } from "../../states/slices/globalReducer";
import { useAppDispatch } from "../../hooks";
import { reset } from "../../states/slices/authReducer";

type RegisterFormValues = z.infer<typeof RegisterSchema>;

function Signup() {
  const [isChecked, setIsChecked] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { register, handleSubmit, setValue, watch } = useForm<
    z.infer<typeof RegisterSchema>
  >({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
    },
  });
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const passwordPattern =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setValue(name as keyof RegisterFormValues, value, { shouldValidate: true });
  };

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();

    setLoading(true);
    const payload: RegisTerTypes = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      password: formData.password,
      password_confirmation: formData.confirmPassword,
    };
    try {
      const response = await RegisterApi(payload);
      if (response) {
        // toast.success(response?.data?.message, {
        //   duration: 10000,
        // });
        dispatch(setSettings(false));
        // dispatch(clearChats());
        navigate("/sign-in", { replace: true });
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          confirmPassword: "",
        });
        dispatch(reset());
        setIsChecked(false);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex items-start w-full overflow-y-clip">
      <div className="w-full flex flex-col items-center justify-center p-10  max-h-[700px] overflow-y-scroll">
        <Fade direction="down" className="w-full mt-10">
          {/* <img src={LOGO_V2} className="w-[221px] h-[41px]" alt="" /> */}
          <div
            className={`flex items-center gap-x-2 cursor-pointer`}
            onClick={() => navigate("")}
          >
            <img
              alt=""
              className={`w-[40.8px] h-[40.8px] cursor-pointer duration-500 transition-all `}
              src={ORANGE_LOGO}
            />
            <span className="text-[23px] font-bold text-[#141718]">
              JudexAI (Judge)
            </span>
          </div>
        </Fade>
        <Bounce className="w-full">
          <div className="w-full lg:px-16 px-0 mt-10">
            <div className="flex flex-col items-start">
              <span className="text-black text-[32px] font-medium">
                Get Started Now
              </span>
              <span className="text-sm font-medium text-red-500">
                {" "}
                Registration Reserved for Anambra State Judges Only.
              </span>
            </div>
            <form
              onSubmit={handleRegister}
              className="flex flex-col gap-y-3 mt-2"
            >
              <div className="flex items-start gap-y-1 flex-col">
                <span>First Name</span>
                <input
                  type="text"
                  {...register("firstName")}
                  value={formData.firstName}
                  disabled
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  className="text-sm placeholder:text-sm placeholder:font-medium font-medium placeholder:text-[#D9D9D9] w-full rounded-lg border border-[#D9D9D9] p-3 outline-none"
                />
              </div>
              <div className="flex items-start gap-y-1 flex-col">
                <span>Last Name</span>
                <input
                  type="text"
                  {...register("lastName")}
                  value={formData.lastName}
                  disabled
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  className="text-sm placeholder:text-sm placeholder:font-medium font-medium placeholder:text-[#D9D9D9] w-full rounded-lg border border-[#D9D9D9] p-3 outline-none"
                />
              </div>
              <div className="flex items-start gap-y-1 flex-col">
                <span>Email address</span>
                <input
                  type="email"
                  {...register("email")}
                  value={formData.email}
                  disabled
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className="text-sm placeholder:text-sm placeholder:font-medium font-medium placeholder:text-[#D9D9D9] w-full rounded-lg border border-[#D9D9D9] p-3 outline-none"
                />
              </div>
              <div className="flex items-start gap-y-1 flex-col">
                <span>Password</span>
                <div className="flex justify-between items-center w-full rounded-lg border  border-[#D9D9D9] p-3">
                  <input
                    type={showPassword1 ? "text" : "password"}
                    {...register("password")}
                    value={formData.password}
                    disabled
                    onChange={handleInputChange}
                    placeholder="........."
                    className="text-sm placeholder:text-sm placeholder:font-medium font-medium placeholder:text-[#D9D9D9] w-full outline-none"
                  />
                  {showPassword1 ? (
                    <FaEyeSlash
                      size={15}
                      className="text-gray-300 cursor-pointer"
                      onClick={() => setShowPassword1(false)}
                    />
                  ) : (
                    <FaEye
                      size={15}
                      className="text-gray-300 cursor-pointer"
                      onClick={() => setShowPassword1(true)}
                    />
                  )}
                </div>
                {formData.password &&
                  !passwordPattern.test(formData.password) && (
                    <span className="text-xs font-normal text-red-500">
                      {" "}
                      Password must contain uppercase, lowercase, a number, and
                      a special character.
                    </span>
                  )}
              </div>
              <div className="flex items-start gap-y-1 flex-col">
                <span>Confirm Password</span>
                <div className="flex justify-between items-center w-full rounded-lg border  border-[#D9D9D9] p-3">
                  <input
                    type={showPassword2 ? "text" : "password"}
                    {...register("confirmPassword")}
                    value={formData.confirmPassword}
                    disabled
                    onChange={handleInputChange}
                    placeholder="........."
                    className="text-sm placeholder:text-sm placeholder:font-medium font-medium placeholder:text-[#D9D9D9] w-full outline-none"
                  />
                  {showPassword2 ? (
                    <FaEyeSlash
                      size={15}
                      className="text-gray-300 cursor-pointer"
                      onClick={() => setShowPassword2(false)}
                    />
                  ) : (
                    <FaEye
                      size={15}
                      className="text-gray-300 cursor-pointer"
                      onClick={() => setShowPassword2(true)}
                    />
                  )}
                </div>
                {formData.confirmPassword &&
                  formData.confirmPassword !== formData.password && (
                    <span className="text-xs font-normal text-red-500">
                      {" "}
                      Confirm password must match your password
                    </span>
                  )}
              </div>
              <div className="flex items-center gap-x-2 mt-4">
                <div
                  className="flex items-center border border-black justify-center w-[12px] h-[12px] rounded cursor-pointer"
                  onClick={() => setIsChecked((prev) => !prev)}
                >
                  {isChecked && <FaCheck size={7} color="#000000" />}
                </div>
                <span className="text-xs font-medium text-black">
                  I agree to the terms & policy
                </span>
              </div>

              <div className="flex items-center mt-4 flex-col gap-y-3 w-full">
                <ButtonV2
                  type="submit"
                  title="Signup"
                  disabled={loading || !isChecked}
                  btnStyle="bg-[#141718] w-full rounded-lg py-3"
                  textStyle="text-white font-medium text-sm"
                  handleClick={() => {}}
                  loading={loading}
                />
                <span className="text-sm font-medium">
                  Have an account?{" "}
                  <span
                    className="text-[#D84C10] cursor-pointer"
                    onClick={() => navigate("/sign-in")}
                  >
                    Sign in
                  </span>
                </span>
              </div>
            </form>
          </div>
        </Bounce>
      </div>
      <Fade direction="right" className="w-full lg:flex hidden">
        <div
          style={{
            backgroundImage: `url(${JUDICIAL_IMAGE})`,
          }}
          className="bg-[#FFF8EB]  w-full h-screen bg-contain bg-no-repeat bg-center rounded-tl-3xl rounded-bl-3xl"
        ></div>
      </Fade>
    </div>
  );
}

export default Signup;
